import { useEffect, useState } from "react";
import {
  PROMO_CONFIG,
  PROMO_DISCOUNT_CONFIG,
  getCurrentPromoPhase,
  getDynamicDiscount,
  type PromoCategoryType,
} from "../shared/promos";

// Re-exports kept for backwards compatibility with the call sites that
// import these names from `useHeruDays`. New code should import directly
// from `shared/promos`.
export type { PromoCategoryType };
export {
  getCampaignBannerMessage,
  getCampaignHeroImage,
  getCampaignModalImage,
  getCurrentPromoPhase,
  getDynamicDiscount,
} from "../shared/promos";

const useHeruDays = (category?: PromoCategoryType) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  const { startDate, endDate, prePromoThresholdDays, postPromoThresholdDays } =
    PROMO_CONFIG;

  const prePromoDate = new Date(startDate);
  prePromoDate.setDate(prePromoDate.getDate() - prePromoThresholdDays);

  const postPromoDate = new Date(endDate);
  postPromoDate.setDate(postPromoDate.getDate() + postPromoThresholdDays);

  const categoryConfig = category
    ? PROMO_DISCOUNT_CONFIG[category]
    : PROMO_DISCOUNT_CONFIG.regularizations;
  const { activePromo } = categoryConfig;

  const percentageDiscount = getDynamicDiscount();

  const getCurrentPromoStatus = () => {
    const now = new Date();

    if (!activePromo) {
      return {
        showHotHeru: false,
        isPrePromo: false,
        isActivePromo: false,
        isPostPromo: false,
      };
    }

    if (now < prePromoDate) {
      return {
        showHotHeru: false,
        isPrePromo: false,
        isActivePromo: false,
        isPostPromo: false,
      };
    }

    if (now >= prePromoDate && now < startDate) {
      return {
        showHotHeru: true,
        isPrePromo: true,
        isActivePromo: false,
        isPostPromo: false,
      };
    }

    if (now >= startDate && now <= endDate) {
      return {
        showHotHeru: true,
        isPrePromo: false,
        isActivePromo: true,
        isPostPromo: false,
      };
    }

    if (now > endDate && now <= postPromoDate) {
      return {
        showHotHeru: false,
        isPrePromo: false,
        isActivePromo: false,
        isPostPromo: true,
      };
    }

    return {
      showHotHeru: false,
      isPrePromo: false,
      isActivePromo: false,
      isPostPromo: false,
    };
  };

  const promoStatus = getCurrentPromoStatus();
  const { showHotHeru, isActivePromo } = promoStatus;

  const getPromoDiscountPercentage = () => {
    return isActivePromo && activePromo ? 1 - percentageDiscount : 1;
  };

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const currentPhase = getCurrentPromoPhase();
      const targetDate = currentPhase ? currentPhase.endDate : endDate;

      const difference = targetDate.getTime() - now.getTime();

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [endDate]);

  return {
    timeLeft,
    showHotHeru,
    getPromoDiscountPercentage,
    promoStatus,
    promoConfig: PROMO_CONFIG,
    percentageDiscount,
    activePromo,
    currentPhase: getCurrentPromoPhase(),
  };
};

export default useHeruDays;
